import { useForm } from "react-hook-form";
import type { FieldErrors } from "react-hook-form";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import CountTextInput from "../../components/UI/CountTextInput";
import Switch from "../../components/UI/Switch";
import React, { useEffect, useState } from "react";
import AddressInput from "../../components/UI/AddressInput";
import TextInput from "../../components/UI/TextInput";
import TextArea from "../../components/UI/TextArea";
import { Button } from "../../components/UI/Button";
import { companyUseCase } from "../../../di/container";
import { useParams } from "react-router-dom";
import { environment } from "../../../infrastructure/config/environment";
import { useNotifications } from "../../hooks/use-notifications";
import NotificationsList from "../../components/UI/NotificationList";
import { CompanyPluginSection } from "../../components/CompanyAdmin/CompanyPlugins/CompanyPluginSection";

const CompanyInfo: React.FC = () => {
  const { companyId } = useParams();

  const {
    notifications,
    addItem: addNotificationItem,
    removeItem,
  } = useNotifications();

  // 이 인터페이스를 업데이트할 경우 다음 파일도
  // 업데이트하여야 함:
  // src/presentation/components/UI/AddressInput.tsx
  interface FormData {
    logoImage: FileList;
    faviconImage: FileList;
    domainLink: string;
    companyName: string;
    ceoName: string;
    phone: string;
    secondaryContactType: string;
    secondaryContact: string;
    zipcode: string;
    address1: string;
    address2: string;
    companySealImage: FileList;
    ecommerceBizNumber: string;
    businessNumber1: string;
    businessNumber2: string;
    businessNumber3: string;
    businessType: string;
    businessItem: string;
    additionalBusinessInfo: string;
    businessCertificate: FileList;
    businessHours: string;
    lunchBreak: string;
    businessHoursNotice: string;
  }

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({ shouldUnregister: false });
  const [errorMessage, setErrorMessage] = useState("");

  const zipcode = watch("zipcode", "");
  const address1 = watch("address1", "");
  const businessCertificateWatch = watch("businessCertificate");

  const [logoUrl, setLogoUrl] = useState("");
  const [businessCertificateUrl, setBusinessCertificateUrl] = useState("");
  // 쇼핑몰 검색 노출 활성화. 파일/텍스트 입력이 아닌 토글이라 react-hook-form 대신 별도 상태로 관리한다.
  const [searchEngineIndexingEnabled, setSearchEngineIndexingEnabled] =
    useState<boolean>(true);

  const logoImageWatch = watch("logoImage");
  const [lastValidLogoFiles, setLastValidLogoFiles] = useState<FileList | null>(
    null,
  );

  useEffect(() => {
    if (logoImageWatch && logoImageWatch.length > 0) {
      setLastValidLogoFiles(logoImageWatch);
    }
  }, [logoImageWatch]);

  const logoPreviewUrl =
    logoImageWatch && logoImageWatch.length > 0
      ? URL.createObjectURL(logoImageWatch[0])
      : logoUrl;

  // 파비콘(브라우저 탭 아이콘). 로고와 달리 선택 항목이라 required 규칙이 없다.
  const [faviconUrl, setFaviconUrl] = useState("");
  const faviconImageWatch = watch("faviconImage");
  const [lastValidFaviconFiles, setLastValidFaviconFiles] =
    useState<FileList | null>(null);

  useEffect(() => {
    if (faviconImageWatch && faviconImageWatch.length > 0) {
      setLastValidFaviconFiles(faviconImageWatch);
    }
  }, [faviconImageWatch]);

  const faviconPreviewUrl =
    faviconImageWatch && faviconImageWatch.length > 0
      ? URL.createObjectURL(faviconImageWatch[0])
      : faviconUrl;

  const [companySealUrl, setCompanySealUrl] = useState("");
  const companySealImageWatch = watch("companySealImage");
  const companySealPreviewUrl =
    companySealImageWatch && companySealImageWatch.length > 0
      ? URL.createObjectURL(companySealImageWatch[0])
      : companySealUrl;

  useEffect(() => {
    // 부모 창에서 메시지 받기
    const handleMessage = (event: MessageEvent<any>) => {
      try {
        const data = event.data;
        console.log("Message received:", data);

        if (data["zipNo"]) {
          setValue("zipcode", data["zipNo"]);
        }
        if (data["roadAddrPart1"] && data["roadAddrPart2"]) {
          setValue(
            "address1",
            `${data["roadAddrPart1"]} ${data["roadAddrPart2"]}`,
          );
        } else if (data["roadAddrPart1"]) {
          setValue("address1", `${data["roadAddrPart1"]}`);
        } else if (data["roadAddrPart2"]) {
          setValue("address1", `${data["roadAddrPart2"]}`);
        }
        if (data["addrDetail"]) {
          setValue("address2", `${data["addrDetail"]}`);
        }
      } catch (e) {}
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [setValue]);

  useEffect(() => {
    (async () => {
      const existingCompanyInfo = await companyUseCase.getCompanyInfo({
        companyId: Number(companyId),
      });

      setCompanySealUrl(existingCompanyInfo.companySealImageUrl ?? "");
      setLogoUrl(existingCompanyInfo.logoUrl ?? "");
      setFaviconUrl(existingCompanyInfo.faviconUrl ?? "");
      setBusinessCertificateUrl(
        existingCompanyInfo.businessRegistrationFileUrl ?? "",
      );

      setValue("zipcode", existingCompanyInfo.zipCode ?? "");
      setValue("domainLink", existingCompanyInfo.domain ?? "");
      // 구버전 백엔드 응답에 필드가 없으면 노출 허용(true)으로 본다
      setSearchEngineIndexingEnabled(
        existingCompanyInfo.searchEngineIndexingEnabled ?? true,
      );
      setValue("companyName", existingCompanyInfo.companyName ?? "");
      setValue("ceoName", existingCompanyInfo.ceoName ?? "");
      setValue("phone", existingCompanyInfo.phone ?? "");
      setValue("secondaryContactType", existingCompanyInfo.secondaryContactType ?? "");
      setValue("secondaryContact", existingCompanyInfo.secondaryContact ?? "");
      setValue("address1", existingCompanyInfo.address ?? "");
      setValue("address2", existingCompanyInfo.addressDetail ?? "");
      setValue(
        "ecommerceBizNumber",
        existingCompanyInfo.ecommerceBizNumber ?? "",
      );
      setValue(
        "businessNumber1",
        existingCompanyInfo.businessNumber?.slice(0, 3) ?? "",
      );
      setValue(
        "businessNumber2",
        existingCompanyInfo.businessNumber?.slice(3, 5) ?? "",
      );
      setValue(
        "businessNumber3",
        existingCompanyInfo.businessNumber?.slice(5, 10) ?? "",
      );
      setValue("businessItem", existingCompanyInfo.businessCategory ?? "");
      setValue("businessType", existingCompanyInfo.businessType ?? "");
      setValue(
        "additionalBusinessInfo",
        existingCompanyInfo.additionalBusinessInfo ?? "",
      );
      // setValue("businessCertificate", existingCompanyInfo.businessRegistrationFileUrl ?? "");

      setValue("businessHours", existingCompanyInfo.businessHours ?? "");
      setValue("lunchBreak", existingCompanyInfo.lunchBreak ?? "");
      setValue("businessHoursNotice", existingCompanyInfo.businessHoursNotice ?? "");
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 주소 정보를 업데이트 할 때 사용하는 함수
  const handleSetZipcode = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const returnUrl = `${environment.apiBaseUrl}/api/address`;
    const url = `https://business.juso.go.kr/addrlink/addrLinkUrl.do?confmKey=${process.env.REACT_APP_JUSO_CONFM_KEY}&returnUrl=${returnUrl}&resultType=1&useDetailAddr=Y&resultType=4`;

    window.open(
      url,
      "jusoPopup",
      "width=570,height=420,scrollbars=yes,resizable=yes",
    );
  };

  const onSubmit = (data: FormData) => {
    if (!companyId) return;
    (async () => {
      const existingCompanyInfo = await companyUseCase.getCompanyInfo({
        companyId: Number(companyId),
      });

      await companyUseCase.updateCompanyInfo({
        address: data.address1,
        addressDetail: data.address2,
        businessCategory: data.businessItem,
        businessNumber:
          data.businessNumber1 + data.businessNumber2 + data.businessNumber3,
        businessType: data.businessType,
        ceoName: data.ceoName,
        companySealImageFile:
          data.companySealImage && data.companySealImage.length > 0
            ? data.companySealImage[0]
            : undefined,
        phone: data.phone,
        secondaryContactType: data.secondaryContactType,
        secondaryContact: data.secondaryContact,
        companyName: data.companyName,
        domain: data.domainLink,
        searchEngineIndexingEnabled,
        zipCode: data.zipcode,
        companyId: existingCompanyInfo.id,
        companyAdminId: existingCompanyInfo.companyAdminId,
        logoFile:
          data.logoImage && data.logoImage.length > 0
            ? data.logoImage[0]
            : undefined,
        faviconFile:
          data.faviconImage && data.faviconImage.length > 0
            ? data.faviconImage[0]
            : undefined,
        businessRegistrationFile:
          data.businessCertificate && data.businessCertificate.length > 0
            ? data.businessCertificate[0]
            : undefined,
        tossPayKey: existingCompanyInfo.tossPayKey ?? "",
        ecommerceBizNumber: data.ecommerceBizNumber,
        additionalBusinessInfo: data.additionalBusinessInfo,
        businessHours: data.businessHours,
        lunchBreak: data.lunchBreak,
        businessHoursNotice: data.businessHoursNotice
      });
    })();

    addNotificationItem("정보가 저장되었습니다.");
  };

  // 아래쪽 취소·저장 버튼이 화면에 보이는지 — 안 보이면 플로팅 버튼을 띄운다
  const actionsRef = React.useRef<HTMLDivElement>(null);
  const [actionsInView, setActionsInView] = useState(true);
  useEffect(() => {
    const target = actionsRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(([entry]) => setActionsInView(entry.isIntersecting), { threshold: 0.5 });
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  const onError = (errors: FieldErrors<FormData>) => {
    const keys = Object.keys(errors) as (keyof FormData)[];

    for (const key of keys) {
      const error = errors[key];
      if (error) {
        setErrorMessage(
          error.message ? String(error.message) : "입력값을 확인해주세요.",
        );
        break;
      }
    }
  };

  return (
    <>
      <>
        <NotificationsList notifications={notifications} />
      </>
      <CompanyAdminLayout>
        <div className="p-5">
          {/*
            플로팅 취소·저장 — 아래쪽 버튼이 화면 밖일 때만 우측 상단에 띄운다.
            본문 스크롤 영역 안에서 sticky 로 붙고, 높이 0 이라 레이아웃을 밀지 않는다.
            폼 밖이라 저장은 form 속성으로 같은 폼을 제출한다.
          */}
          <div className="sticky top-3 z-[60] h-0 flex items-start justify-end pointer-events-none">
            <div
              aria-hidden={actionsInView}
              className={`shrink-0 flex items-center gap-2 transition-all duration-200 ${
                actionsInView ? "opacity-0 -translate-y-2" : "opacity-100 translate-y-0 pointer-events-auto"
              }`}
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                tabIndex={actionsInView ? -1 : 0}
                onClick={() => window.location.reload()}
                className="w-[4.5rem] shadow-[0_4px_14px_rgba(0,0,0,0.12)]"
              >
                취소
              </Button>
              <Button
                type="submit"
                form={FORM_ID}
                variant="primary"
                size="sm"
                tabIndex={actionsInView ? -1 : 0}
                className="w-[4.5rem] shadow-[0_4px_14px_rgba(66,133,244,0.35)]"
              >
                저장
              </Button>
            </div>
          </div>
          <form
            id={FORM_ID}
            className="flex-col overflow-hidden text-[14px]"
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <div className="w-full / flex flex-col md:flex-row md:justify-between items-start gap-1">
              <h3 className="font-semibold">기본 정보</h3>
              <p className="text-start text-[#757575] ">
                배송 혹은 긴급 연락 시 사용되는 정보입니다.
              </p>
            </div>

            <div className="mb-[2.0313rem]" />

            <div
              className="pl-0 md:pl-5 / grid grid-cols-1 gap-[1.25rem] md:gap-4
          / text-[14px] text-left / w-full max-w-[45rem]"
            >
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px]">
                <div>로고이미지</div>
                <div className="flex flex-col sm:flex-row gap-[1.5rem]">
                  <div>
                    <div className="w-[12.5rem]">
                      <label>
                        <div
                          className={`border ${
                            errors.logoImage?.message
                              ? "border-[#F24A4A]"
                              : "border-[#d9d9d9]"
                          }
                  / rounded-[0.5rem] / w-[12.5rem] h-[12.5rem]
                  / bg-gray-50 hover:bg-[#e6e6e6] transition-all relative`}
                        >
                          {logoPreviewUrl ? (
                            <img
                              src={logoPreviewUrl}
                              alt="로고 미리보기"
                              className="object-contain w-full h-full"
                            />
                          ) : null}
                          {errors.logoImage?.message ? (
                            <div className="flex items-center absolute bottom-[0.7188rem] right-[0.7188rem]">
                              <img
                                className="w-[1.2213rem]"
                                alt="alert icon"
                                src="/assets/icons/alerticon.svg"
                              />
                            </div>
                          ) : null}
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".png,.jpg,.jpeg"
                          {...register("logoImage", {
                            required: {
                              value: Boolean(!logoUrl),
                              message: "필수 항목이에요",
                            },
                            onChange: (e) => {
                              const fileList = e.target.files;
                              if (!fileList || fileList.length === 0) {
                                if (
                                  lastValidLogoFiles &&
                                  lastValidLogoFiles.length > 0
                                ) {
                                  setValue("logoImage", lastValidLogoFiles);
                                }
                              }
                            },
                          })}
                        />
                        <div className="size-min">
                          {logoImageWatch && logoImageWatch.length > 0 && (
                            <div className="mt-2">{logoImageWatch[0].name}</div>
                          )}
                        </div>
                      </label>
                    </div>

                    <div className="text-[12px] mt-[0.25rem] text-[#F24A4A]">
                      {errors.logoImage?.message}
                    </div>

                    <ul className="text-[0.75rem] text-[#757575] mt-2">
                      <li>· 쇼핑몰 정보에 노출될 대표이미지입니다.</li>
                      <li>
                        · 이미지 권장 사이즈는 180x50px입니다. (png, jpg, jpeg)
                      </li>
                      <li>
                        · 사이즈 안에서 이미지가 잘려보이지 않게 좌우상하 여백
                        확보를 권장합니다.
                      </li>
                    </ul>
                  </div>

                  {/* 파비콘 — 쇼핑몰 브라우저 탭 아이콘. 로고 우측에 나란히 둔다. */}
                  <div>
                    <div className="mb-[0.5rem] text-[#555]">파비콘</div>
                    <div className="w-[12.5rem]">
                      <label>
                        <div
                          className="border border-[#d9d9d9]
                  / rounded-[0.5rem] / w-[7.5rem] h-[7.5rem]
                  / bg-gray-50 hover:bg-[#e6e6e6] transition-all relative
                  / flex items-center justify-center"
                        >
                          {faviconPreviewUrl ? (
                            <img
                              src={faviconPreviewUrl}
                              alt="파비콘 미리보기"
                              className="object-contain w-full h-full p-2"
                            />
                          ) : (
                            <span className="text-[0.75rem] text-[#bbb]">
                              선택 안 함
                            </span>
                          )}
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".png,.ico,.svg,image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml"
                          {...register("faviconImage", {
                            onChange: (e) => {
                              const fileList = e.target.files;
                              if (!fileList || fileList.length === 0) {
                                if (
                                  lastValidFaviconFiles &&
                                  lastValidFaviconFiles.length > 0
                                ) {
                                  setValue(
                                    "faviconImage",
                                    lastValidFaviconFiles,
                                  );
                                }
                              }
                            },
                          })}
                        />
                        <div className="size-min">
                          {faviconImageWatch && faviconImageWatch.length > 0 && (
                            <div className="mt-2 break-all">
                              {faviconImageWatch[0].name}
                            </div>
                          )}
                        </div>
                      </label>
                    </div>

                    <ul className="text-[0.75rem] text-[#757575] mt-2 max-w-[12.5rem]">
                      <li>· 쇼핑몰 브라우저 탭에 표시되는 아이콘입니다.</li>
                      <li>· 권장 사이즈는 32x32px 또는 64x64px 정사각형입니다. (png, ico, svg)</li>
                      <li>· 지정하지 않으면 POD24 기본 파비콘이 사용됩니다.</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-center">
                <div>도메인 주소</div>
                <div className="flex flex-col md:flex-row md:items-center gap-[12px]">
                  <div className="flex-1">
                    <CountTextInput
                      className="bg-gray-50"
                      errorMessage={errors.domainLink?.message}
                      {...register("domainLink", {
                        required: {
                          value: true,
                          message: "필수 항목이에요",
                        },
                      })}
                      maxLength={50}
                      placeholder="쇼핑몰에 표시될 도메인명을 입력해 주세요."
                    />
                  </div>
                  {/* 쇼핑몰 검색 노출 활성화: 도메인은 연결했지만 정식 오픈 전인 쇼핑몰이 검색 결과에 뜨지 않도록 끌 수 있다. */}
                  <div
                    role="switch"
                    aria-checked={searchEngineIndexingEnabled}
                    aria-label="쇼핑몰 검색 노출 활성화"
                    className="flex flex-row items-center gap-[8px] shrink-0 cursor-pointer select-none"
                    onClick={() => {
                      setSearchEngineIndexingEnabled((prev) => !prev);
                      addNotificationItem(
                        "검색 노출 설정은 저장 후 검색엔진이 쇼핑몰을 다시 방문해야 반영되며, 즉시 적용되지 않습니다.",
                      );
                    }}
                  >
                    <span className="text-[0.875rem] whitespace-nowrap">
                      쇼핑몰 검색 노출 활성화
                    </span>
                    <Switch isEnabled={searchEngineIndexingEnabled} />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-center">
                <div>업체명</div>
                <div>
                  <CountTextInput
                    className="bg-gray-50"
                    errorMessage={errors.companyName?.message}
                    {...register("companyName", {
                      required: {
                        value: true,
                        message: "필수 항목이에요",
                      },
                    })}
                    maxLength={50}
                    placeholder="쇼핑몰에 표시될 업체명을 입력해 주세요."
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-center">
                <div>대표자명</div>
                <div>
                  <CountTextInput
                    className="bg-gray-50"
                    errorMessage={errors.ceoName?.message}
                    {...register("ceoName", {
                      required: {
                        value: true,
                        message: "필수 항목이에요",
                      },
                    })}
                    maxLength={50}
                    placeholder="쇼핑몰에 표시될 대표자명을 입력해 주세요."
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-center">
                <div>전화번호</div>
                <div>
                  <TextInput
                    {...register("phone", {
                      required: {
                        value: true,
                        message: "필수 항목이에요.",
                      },
                    })}
                    error={{
                      message: errors.phone?.message || "",
                      isIconVisible: true,
                      isMessageVisible: true,
                    }}
                    placeholder="휴대폰 번호 입력"
                    type="number"
                    innerClassName="bg-gray-50"
                    inputClassName="no-spinner"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-center">
                <div>보조 연락처</div>
                <div className="flex flex-row gap-2">
                  <div className="w-[8.75rem]">
                    <TextInput
                      {...register("secondaryContactType", {
                        required: {
                          value: false,
                          message: "필수 항목이에요.",
                        },
                      })}
                      error={{
                        message: errors.secondaryContactType?.message || "",
                        isIconVisible: false,
                        isMessageVisible: true,
                      }}
                      placeholder="ex. FAX, 상담 전화번호"
                      innerClassName="bg-gray-50"
                    />
                  </div>

                  <TextInput
                    {...register("secondaryContact", {
                      required: {
                        value: false,
                        message: "필수 항목이에요.",
                      },
                    })}
                    error={{
                      message: errors.secondaryContact?.message || "",
                      isIconVisible: true,
                      isMessageVisible: false,
                    }}
                    placeholder="ex. 02-0000-0000"
                    innerClassName="bg-gray-50"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-center">
                <div>주소</div>
                <div>
                  <AddressInput
                    errorMessage={
                      errors.zipcode?.message ||
                      errors.address1?.message ||
                      errors.address2?.message
                    }
                    zipCodeWatch={zipcode}
                    address1Watch={address1}
                    address2MaxLength={300}
                    register={register}
                    onZipCodeButtonClick={handleSetZipcode}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px]">
                <div>인감 도장 이미지</div>
                <div>
                  <div className="w-[12.5rem]">
                    <label className="size-fit">
                      <div
                        className={`border ${
                          errors.companySealImage?.message
                            ? "border-[#F24A4A]"
                            : "border-[#d9d9d9]"
                        }
                  / rounded-[0.5rem] / w-[12.5rem] h-[12.5rem]
                  / bg-gray-50 hover:bg-[#e6e6e6] transition-all relative`}
                      >
                        {companySealPreviewUrl ? (
                          <img
                            src={companySealPreviewUrl}
                            alt="인감 도장 미리보기"
                            className="object-contain w-full h-full"
                          />
                        ) : null}
                        {errors.companySealImage?.message ? (
                          <div className="flex items-center absolute bottom-[0.7188rem] right-[0.7188rem]">
                            <img
                              className="w-[1.2213rem]"
                              alt="alert icon"
                              src="/assets/icons/alerticon.svg"
                            />
                          </div>
                        ) : null}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept=".png,.jpg,.jpeg"
                        {...register("companySealImage", {
                          required: {
                            value: Boolean(!companySealPreviewUrl),
                            message: "필수 항목이에요",
                          },
                        })}
                      />
                      <div className="size-min">
                        {companySealImageWatch &&
                          companySealImageWatch.length > 0 && (
                            <div className="mt-2">
                              {companySealImageWatch[0].name}
                            </div>
                          )}
                      </div>
                    </label>
                  </div>
                  <div className="text-[12px] mt-[0.25rem] text-[#F24A4A]">
                    {errors.companySealImage?.message}
                  </div>

                  <ul className="text-[0.75rem] text-[#757575] mt-2">
                    <li>· 거래명세서에 사용될 이미지입니다.</li>
                    <li>· 이미지 권장 사이즈는 300x300px입니다. (png)</li>
                    <li>· 투명한 이미지 배경을 사용해주세요.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border border-[#D4D4D4] w-full my-[2.0313rem]"></div>

            <div className="w-full / flex flex-col md:flex-row md:justify-between items-start gap-1 mb-[2.0313rem]">
              <h3 className="font-semibold">업종 정보</h3>
              {/* <p className="text-start text-[#757575] text-[14px]">
              설명
            </p> */}
            </div>

            <div
              className="pl-0 md:pl-5 / grid grid-cols-1 gap-[1.25rem] md:gap-4
          / text-[14px] text-left / w-full max-w-[45rem] mb-[2.0313rem]"
            >
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-center">
                <div>통신판매업신고번호</div>
                <div>
                  <TextInput
                    innerClassName="bg-gray-50"
                    error={{
                      message: errors.ecommerceBizNumber?.message ?? "",
                      isIconVisible: true,
                      isMessageVisible: true,
                    }}
                    variant="admin"
                    {...register("ecommerceBizNumber", {
                      required: {
                        value: true,
                        message: "필수 항목이에요",
                      },
                    })}
                    maxLength={30}
                    placeholder="예) 2025-서울서대문-0001"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-center">
                <div>사업자 등록번호, 고유번호</div>
                <div>
                  <div className="flex flex-row items-start gap-[0.5rem]">
                    <TextInput
                      innerClassName="bg-gray-50"
                      error={{
                        message:
                          errors.businessNumber1?.message ||
                          errors.businessNumber2?.message ||
                          errors.businessNumber3?.message ||
                          "",
                        isIconVisible: false,
                        isMessageVisible: true,
                      }}
                      variant="admin"
                      {...register("businessNumber1", {
                        required: {
                          value: true,
                          message: "필수 항목이에요",
                        },
                      })}
                      maxLength={3}
                      placeholder="000"
                    />
                    <div className="mt-3">-</div>
                    <TextInput
                      innerClassName="bg-gray-50"
                      error={{
                        message:
                          errors.businessNumber1?.message ||
                          errors.businessNumber2?.message ||
                          errors.businessNumber3?.message ||
                          "",
                        isIconVisible: false,
                        isMessageVisible: false,
                      }}
                      variant="admin"
                      {...register("businessNumber2", {
                        required: {
                          value: true,
                          message: "필수 항목이에요",
                        },
                      })}
                      maxLength={2}
                      placeholder="00"
                    />
                    <div className="mt-3">-</div>
                    <TextInput
                      innerClassName="bg-gray-50"
                      error={{
                        message:
                          errors.businessNumber1?.message ||
                          errors.businessNumber2?.message ||
                          errors.businessNumber3?.message ||
                          "",
                        isIconVisible: true,
                        isMessageVisible: false,
                      }}
                      variant="admin"
                      {...register("businessNumber3", {
                        required: {
                          value: true,
                          message: "필수 항목이에요",
                        },
                      })}
                      maxLength={5}
                      placeholder="00000"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-center">
                <div>업태</div>
                <div>
                  <CountTextInput
                    className="bg-gray-50"
                    errorMessage={errors.businessType?.message}
                    {...register("businessType", {
                      required: {
                        value: true,
                        message: "필수 항목이에요",
                      },
                    })}
                    maxLength={50}
                    placeholder="예) 제조업, 소매업, 서비스업 등"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-center">
                <div>종목</div>
                <div>
                  <CountTextInput
                    className="bg-gray-50"
                    errorMessage={errors.businessItem?.message}
                    {...register("businessItem", {
                      required: {
                        value: true,
                        message: "필수 항목이에요.",
                      },
                    })}
                    maxLength={50}
                    placeholder="예) 복사, 제본, 인쇄, 문구 등"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] / items-start">
                <div className="md:mt-[0.75rem]">추가 사업자 정보</div>
                <div>
                  <TextArea
                    variant="admin"
                    className="bg-gray-50 rounded-[8px]"
                    rows={3}
                    maxLength={500}
                    {...register("additionalBusinessInfo")}
                    placeholder="예) 서충지점 | 상호 : OOO | 대표 : OOO | 사업자등록번호 : 000-00-00000"
                  />
                  <ul className="text-[0.75rem] text-[#757575] mt-2">
                    <li>
                      · 쇼핑몰 푸터의 사업자등록번호 줄 아래에 입력한 그대로 표시되는 안내 문구입니다.
                    </li>
                    <li>
                      · 지점 등 사업자 정보를 한 줄에 하나씩 적어주세요. 줄바꿈한 만큼 줄이 늘어납니다.
                    </li>
                    <li>
                      · 결제·세금계산서 등 실제 처리에는 위의 사업자 등록번호만 사용됩니다.
                    </li>
                    <li>· 비워두면 푸터에 아무것도 표시되지 않습니다.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-[2.0313rem] relative">
              <div
                className={`rounded-[0.5rem] ${
                  errors.businessCertificate?.message
                    ? "border border-[#F24A4A]"
                    : ""
                }`}
              >
                <div className="bg-[#EAEAEA] rounded-[0.625rem]">
                  <div className="flex flex-col md:flex-row md:justify-between p-[1.2rem]">
                    <div>
                      사업자 등록증 또는 고유번호증
                      <span className="text-[#F24A4A]">*</span>
                    </div>
                    <div className="text-[0.75rem] text-[#757575]">
                      지원파일: JPG, JPEG, PNG, PDF(최대 10MB)
                    </div>
                  </div>

                  <div className="w-full border-[0.0313rem] border-[#D4D4D4]" />

                  <div className="flex flex-col p-[1.2rem]">
                    <div className="flex items-center gap-2 justify-start w-full">
                      <label className="size-fit flex">
                        <div className="bg-white px-[0.75rem] py-[0.5rem] rounded-[0.25rem] border border-[#D9D9D9] hover:bg-[#e6e6e6] transition-all">
                          파일 선택
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".png,.jpg,.jpeg,.pdf"
                          {...register("businessCertificate", {
                            required: {
                              value: !Boolean(businessCertificateUrl),
                              message: "필수 항목이에요.",
                            },
                          })}
                        />
                      </label>
                      <div>
                        {businessCertificateWatch &&
                        businessCertificateWatch.length > 0 ? (
                          <div>{businessCertificateWatch[0].name}</div>
                        ) : (
                          <>
                            {businessCertificateUrl
                              ? new URL(businessCertificateUrl).pathname
                                  .split("/")
                                  .pop()
                              : null}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {errors.businessCertificate?.message ? (
                  <div className="flex items-center absolute bottom-9 right-[0.7188rem]">
                    <img
                      className="w-[1.2213rem]"
                      alt="alert icon"
                      src="/assets/icons/alerticon.svg"
                    />
                  </div>
                ) : null}
              </div>

              <div className="text-start text-[0.75rem] mt-[0.25rem] text-[#F24A4A]">
                {errors.businessCertificate?.message}
              </div>
            </div>

            <div className="border border-[#D4D4D4] w-full my-[2.0313rem]"></div>

            {/*
              채널톡 키 / 홈택스 키 텍스트필드였던 자리. 이제 외부 서비스는 플러그인 카드로 등록한다(요청 5).
              플러그인은 이 폼의 '저장' 과 별개로 다이얼로그에서 저장 즉시 반영된다.
            */}
            {companyId && (
              <CompanyPluginSection
                companyId={Number(companyId)}
                onNotify={addNotificationItem}
              />
            )}

            <div className="border border-[#D4D4D4] w-full my-[2.0313rem]"></div>

            <div className="w-full / flex flex-col md:flex-row md:justify-between items-start gap-1 mb-[2.0313rem]">
              <h3 className="font-semibold">영업시간</h3>
            </div>

            <div
              className="pl-0 md:pl-5 / grid grid-cols-2 lg:grid-cols-3 gap-[1.25rem] md:gap-[30px]
          / text-[14px] text-left / w-full max-w-[45rem] mb-[2.0313rem]"
            >
              <div className="grid grid-cols-1 md:grid-cols-[1fr_2.5fr] gap-[16px] / items-center">
                <div>영업 시간</div>
                <div>
                  <TextInput
                    innerClassName="bg-gray-50"
                    error={{
                      message: errors.businessHours?.message || "",
                      isIconVisible: false,
                      isMessageVisible: true,
                    }}
                    variant="admin"
                    {...register("businessHours", {
                      required: {
                        value: false,
                        message: "필수 항목이에요",
                      },
                    })}
                    className="max-w-[220px] sm:max-w-none"
                    placeholder="09:00~ 18:00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_2.5fr] gap-[16px] / items-center">
                <div>점심 시간</div>
                <div>
                  <TextInput
                    innerClassName="bg-gray-50"
                    error={{
                      message: errors.lunchBreak?.message || "",
                      isIconVisible: false,
                      isMessageVisible: true,
                    }}
                    variant="admin"
                    {...register("lunchBreak", {
                      required: {
                        value: false,
                        message: "필수 항목이에요",
                      },
                    })}
                    className="max-w-[220px] sm:max-w-none"
                    placeholder="12:00~ 13:00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_2.5fr] gap-[16px] / items-center">
                <div>제외</div>
                <div>
                  <TextInput
                    innerClassName="bg-gray-50"
                    error={{
                      message: errors.businessHoursNotice?.message || "",
                      isIconVisible: false,
                      isMessageVisible: true,
                    }}
                    variant="admin"
                    {...register("businessHoursNotice", {
                      required: {
                        value: false,
                        message: "필수 항목이에요",
                      },
                    })}
                    className="max-w-[220px] sm:max-w-none"
                    placeholder="주말, 공휴일은 제외"
                  />
                </div>
              </div>
            </div>

            <div ref={actionsRef} className="flex justify-center gap-[0.75rem]">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => {
                  window.location.reload();
                }}
                className="w-[8.75rem]"
              >
                취소
              </Button>
              <Button
                className="w-[8.75rem]"
                type="submit"
                variant="primary"
                size="lg"
              >
                저장
              </Button>
            </div>

            <div className="mb-[100px]"></div>
          </form>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

const FORM_ID = "company-info-form";

export default CompanyInfo;
